/**
 * Katalog kolorów koszulek — wspólny dla storefrontu i podglądu na makiecie.
 *
 * `value` musi być dokładnie tą wartością, którą trzyma opcja "Kolor"
 * w Medusie (patrz backend/src/scripts/add-shirt-colors.ts). Kolor materiału
 * jest przybliżeniem palety Fruit of the Loom Valueweight T — to podgląd,
 * a nie wzornik: monitor i tak pokaże co innego niż tkanina.
 *
 * `dark` mówi, czy na tej koszulce nadruk monochromatyczny drukujemy białym
 * tuszem. Czarna kreska na granacie czy czerni po prostu nie istnieje, więc
 * przy tych kolorach odwracamy grafiki oznaczone jako `print_mode: "mono"`.
 * Grafiki kolorowe zostają bez zmian (odwrócenie zamieniłoby sepię w błękit).
 */
export type ShirtColor = {
  /** Wartość opcji "Kolor" w Medusie. */
  value: string
  /** Kolor tkaniny na makiecie. */
  hex: string
  /** Ciemniejszy odcień na szwy i fałdy. */
  seam: string
  /** Czy nadruk mono idzie białym tuszem. */
  dark: boolean
}

/** Tytuł opcji produktu, w której trzymamy kolor. */
export const COLOR_OPTION_TITLE = "Kolor"

export const SHIRT_COLORS: ShirtColor[] = [
  { value: "Biały", hex: "#F2F0EA", seam: "#D6D2C8", dark: false },
  { value: "Czarny", hex: "#1C1C1C", seam: "#000000", dark: true },
  { value: "Niebieski", hex: "#24408E", seam: "#152860", dark: true },
  { value: "Błękitny", hex: "#8FC4E3", seam: "#6698B7", dark: false },
  { value: "Zielony", hex: "#187A45", seam: "#0D5530", dark: true },
  { value: "Czerwony", hex: "#C4162B", seam: "#8B0E1D", dark: true },
]

/** Kolejność kolorów w UI — taka, jak w katalogu powyżej, niezależnie od bazy. */
export const COLOR_ORDER = SHIRT_COLORS.map((c) => c.value)

export const findShirtColor = (value?: string | null): ShirtColor | undefined =>
  SHIRT_COLORS.find((c) => c.value === value)

export const isColorOption = (title?: string | null): boolean =>
  (title ?? "").trim().toLowerCase() === COLOR_OPTION_TITLE.toLowerCase()

/**
 * Domyślny kolor dla produktu: pierwszy z katalogu, który produkt faktycznie ma.
 * Biały wygrywa, bo na nim każda grafika — mono i kolorowa — wygląda tak,
 * jak ją zaprojektowano.
 */
export const defaultColorFor = (
  options?: { title?: string | null; values?: { value: string }[] | null }[] | null
): string | undefined => {
  const option = (options ?? []).find((o) => isColorOption(o.title))
  if (!option) {
    return undefined
  }

  const available = (option.values ?? []).map((v) => v.value)
  return COLOR_ORDER.find((c) => available.includes(c)) ?? available[0]
}

/**
 * Tryb nadruku bierzemy z `metadata.print_mode` produktu (ustawia go
 * backend/src/scripts/set-shirt-specs.ts). Domyślnie "color" — bezpieczniej
 * pokazać grafikę taką, jaka jest, niż odwrócić kolorową.
 */
export const printModeOf = (metadata?: Record<string, unknown> | null) =>
  metadata?.print_mode === "mono" ? "mono" : "color"
